/**
 * Language for the staff-facing screens (My Performance today).
 *
 * English strings are the keys — wrap a literal in `t('Final score')` and the
 * provider swaps it for the chosen language. The map is fetched once per
 * language from `/api/i18n/ui` and cached on the server, so switching is a
 * single fast call.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { UI_STRINGS } from './uiStrings';

export const LANGUAGES = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'hi', label: 'Hindi', native: 'हिन्दी' },
  { code: 'bn', label: 'Bengali', native: 'বাংলা' },
  { code: 'kn', label: 'Kannada', native: 'ಕನ್ನಡ' },
];

const KEY = 'unisol.lang';
const LangContext = createContext({ lang: 'en', setLang: () => {}, t: (s) => s, loading: false });

export const LangProvider = ({ children }) => {
  const { authHeader } = useAuth();
  const [lang, setLangState] = useState(() => localStorage.getItem(KEY) || 'en');
  const [maps, setMaps] = useState({});
  const [loading, setLoading] = useState(false);

  const setLang = useCallback((next) => {
    localStorage.setItem(KEY, next);
    setLangState(next);
  }, []);

  useEffect(() => {
    if (lang === 'en' || maps[lang]) return;
    let alive = true;
    setLoading(true);
    axios.post(`${API}/i18n/ui`, { lang, texts: UI_STRINGS }, { headers: authHeader(), timeout: 90000 })
      .then(({ data }) => { if (alive) setMaps((m) => ({ ...m, [lang]: data.map || {} })); })
      .catch(() => { if (alive) setMaps((m) => ({ ...m, [lang]: {} })); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [lang, maps, authHeader]);

  const t = useCallback(
    (s) => (lang === 'en' ? s : ((maps[lang] || {})[s] || s)),
    [lang, maps],
  );

  const value = useMemo(() => ({ lang, setLang, t, loading }), [lang, setLang, t, loading]);
  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);

export default LangProvider;
